import { useConfigInputs, useMetrics, usePricing, useVendorCtx, useCalcStatus } from 'shared/state/configContexts';
import { useQuoteActionsCtx } from 'shared/state/quoteActionsContext';
import { useAbility, PERM } from 'shared/access';
import { formatMoney, formatPerTb } from 'shared/lib/format';

/** One Solution readout. `current` is present only when a baseline exists —
 *  see the Current/Custom pairing below (SpecialPricingMetrics.jsx). */
export type SolutionFigureSpec = { id: string; label: string; value: string; current?: string; tone?: 'brand' };

type SolutionTotals = { grandTotal?: number; softwareTotal?: number };
type Baseline = { pricingData: { solution: SolutionTotals }; efficiency: Record<string, number> | null } | null;
type Metrics = Record<string, unknown> | null;

const pctOf = (software: unknown, grand: unknown) =>
  Number(((Number(software) / Number(grand)) * 100) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

/** base/baseEfficiency exactly as SpecialPricingMetrics.jsx derives them: null
 *  (the common case) once nothing has moved off list/catalog pricing. */
function selectBaseline(baselinePricing: unknown): { base: SolutionTotals | null; baseEfficiency: Record<string, number> | null } {
  const b = baselinePricing as Baseline;
  const base = b?.pricingData?.solution ?? null;
  return { base, baseEfficiency: base ? (b?.efficiency ?? null) : null };
}

/** The Solution figures, each carrying its live (Custom) value and, once a
 *  baseline exists, the at-list (Current) counterpart — reproducing
 *  SpecialPricingMetrics' tile() pairing. Split out of the hook body so its
 *  ternaries don't inflate the hook's own complexity. */
function buildFigures(opts: {
  softwareOnly: boolean; live: SolutionTotals; base: SolutionTotals | null;
  baseEfficiency: Record<string, number> | null; metrics: Metrics;
}): { figures: SolutionFigureSpec[]; liveGrand: number } {
  const { softwareOnly, live, base, baseEfficiency, metrics } = opts;
  // No `?? 0` on either $/TB reader: an ABSENT key must reach formatPerTb as
  // NaN so it renders '—', which is what classic's per() does
  // (SpecialPricingMetrics.jsx) — `?? 0` rendered a confident "$0.00" instead.
  // A zero-capacity config's Infinity survives untouched either way.
  const num = (k: string) => Number(metrics?.[k]);
  // Classic (SpecialPricingMetrics.jsx:105) picks the whole object ONCE —
  // `perTB(baseEfficiency ?? metrics)` — and reads every key off that same
  // object, rather than falling back per-key. Falling back per-key would let
  // a present-but-partial baseEfficiency silently borrow the LIVE value for a
  // missing key and label it "Current", which is a wrong number under a
  // parity claim.
  const baseEff = baseEfficiency ?? metrics;
  // CONF-258: exec metric overrides now only ever target per-node throughput
  // ratings, which never move the $/TB (capacity) denominators — the
  // capacity-override path that once paired the $/TB figures off
  // baselineMetrics with no pricing baseline is retired. Current vs Custom
  // for $/TB comes from a PRICING baseline only, same as every other figure.
  const perTbCurrent: Metrics = base ? baseEff : null;
  const perTbCur = (k: string) => (perTbCurrent ? formatPerTb(Number(perTbCurrent[k])) : undefined);

  const liveGrand = Number((softwareOnly ? live.softwareTotal : live.grandTotal) ?? 0);
  const baseGrand = base ? Number((softwareOnly ? base.softwareTotal : base.grandTotal) ?? 0) : undefined;

  const figures: SolutionFigureSpec[] = [
    {
      id: 'solution', label: softwareOnly ? 'VDURA Pricing' : 'Solution Price',
      value: formatMoney(liveGrand), current: base ? formatMoney(baseGrand as number) : undefined, tone: 'brand',
    },
    {
      id: 'perRaw', label: '$ / RAW TB',
      value: formatPerTb(num('dollarsPerRawTB')), current: perTbCur('dollarsPerRawTB'),
    },
    {
      id: 'perUseable', label: '$ / Useable TB',
      value: formatPerTb(num('dollarsPerEffectiveTB')), current: perTbCur('dollarsPerEffectiveTB'),
    },
    {
      id: 'perEffective', label: '$ / Effective TB',
      value: formatPerTb(num('dollarsPerCompressedEffectiveTB')), current: perTbCur('dollarsPerCompressedEffectiveTB'),
    },
  ];
  if (!softwareOnly) {
    const liveCarePct = pctOf(live.softwareTotal, live.grandTotal);
    const baseCarePct = base ? pctOf(base.softwareTotal, base.grandTotal) : undefined;
    figures.push({ id: 'carePct', label: 'VDURA Care + Install', value: `${liveCarePct} %`, current: base ? `${baseCarePct} %` : undefined });
  }
  return { figures, liveGrand };
}

/** §5.5 / App.js:1288-1309: term-based vendors price software per TB for the
 *  whole term, but Special Pricing still accepts the familiar $/10TB/month
 *  figure — this hint is the only place the actual per-TB unit price the
 *  quote is built from stays visible to the SE. No hint at all off term-based
 *  vendors, and none for Discount Months either way. */
function buildRateHints(sp: { swModel?: { termBased?: boolean }; swModelMonths?: number }, pricing: { baseSsdSoftware: number; baseHddSoftware: number }) {
  const termBased = Boolean(sp.swModel?.termBased);
  if (!termBased) return { termBased, ssdRateHint: undefined, hddRateHint: undefined };
  const termMonths = sp.swModelMonths;
  const hint = (base: number) =>
    `= $${Number(base).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per TB for the ${termMonths}-month term`;
  return { termBased, ssdRateHint: hint(pricing.baseSsdSoftware), hddRateHint: hint(pricing.baseHddSoftware) };
}

/**
 * Pricing section state: the Solution figures every pricing user sees, plus
 * the Rates group (SSD/HDD software $, discount months) gated to RATES_EDIT.
 *
 * Current vs Custom (spec §6 Phase 4): useConfigurationLogic prices the
 * solution WITH the exec's custom hardware costs and Special Pricing's
 * software rates applied, so once either lever moves the live figures are
 * silently the CUSTOM ones. `buildFigures` reproduces SpecialPricingMetrics'
 * selection so a baseline pairs Current (at-list) against Custom (live)
 * exactly as the classic card does.
 */
export function usePricingSection() {
  const { pricing, viewState } = useConfigInputs();
  const metrics = useMetrics();
  const { pricingData, baselinePricing } = usePricing();
  const { vendor } = useVendorCtx();
  const { isCalculating } = useCalcStatus();
  const { sp } = useQuoteActionsCtx();
  const ability = useAbility();

  const softwareOnly = vendor.businessRules.softwareSubscriptionOnly === true;
  const live: SolutionTotals = pricingData?.solution ?? {};
  const { base, baseEfficiency } = selectBaseline(baselinePricing);
  const { figures, liveGrand } = buildFigures({ softwareOnly, live, base, baseEfficiency, metrics });

  const showRates = ability.can(PERM.RATES_EDIT);
  const { termBased, ssdRateHint, hddRateHint } = buildRateHints(sp, pricing);

  return {
    isCalculating,
    figures,
    // §4.9: the toggle follows pricing rights, and appears in exactly one group.
    registeredIn: (showRates ? 'rates' : 'solution') as 'rates' | 'solution',
    registered: !viewState.isNoReg,
    onRegisteredToggle: sp.handleRegToggle as () => void,
    showRates,
    rates: sp,
    termBased,
    ssdRateHint,
    hddRateHint,
    baseline: baselinePricing,
    summary: formatMoney(liveGrand),
  };
}
